import { getGeoData } from 'api/geo'
import { API_CALL } from './actionConstants'
import { FETCH_CURRENCY_CONVERSION_RATE, FETCH_GEO_AIRPORTS, FETCH_GEO_DATA } from './apiActionConstants'
import { getAirports } from 'api/flights'
import { convertCurrency } from 'api/fx'
import noop from 'lib/function/noop'
import { getCurrencyConversionRateKey } from 'lib/geo/geoUtils'
import type { ApiActionResult } from 'middlewares/apiMiddleware'
import type { AppAction } from './types/ActionTypes'
/**
 * Fetches the geo information required for the app to run
 * @param regionCode The region code to load geo data for
 * @param initialiseOnFailure Whether or not to set the state as initialised if we had a failure
 * @returns
 */
export function fetchGeoState(regionCode: string, initialiseOnFailure?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.geo.initialised) {
      // already initialised, no need to do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_GEO_DATA,
      request: () => getGeoData(regionCode),
      initialiseOnFailure,
    })
  }
}

export function fetchGeoAirports(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (state.geo.initialised) {
      // already initialised, no need to do it again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_GEO_AIRPORTS,
      request: () => Promise.all([
      /**
       * Airports are region agnostic data, but svc-flights limits them behind certain
       * regions. So we'll get around this by hard coding the region to an enabled region
       * This fixes issues where airports are used for something other than flights (e.g. transfers)
       */
        state.geo.currentRegionCode === 'AU' ? Promise.resolve({} as any) : getAirports('AU'),
        getAirports(state.geo.currentRegionCode),
      ]).then(([allAirportResponse, airportData]) => {
        return {
          ...airportData,
          airports: allAirportResponse.airports ?? airportData.airports,
        }
      }),
    })
  }
}

// svc-fx lets its rates be cached for an hour, so a stored rate is reused for as long
const CURRENCY_CONVERSION_RATE_MAX_AGE_MS = 60 * 60 * 1000

/**
 * Fetches how many units of the target currency one unit of the base currency buys, at svc-fx's v2
 * rates, into `geo.currencyConversionRates`. Reuses a rate fetched within the last hour.
 * @param onComplete Called with the rate once it's known; the only way to wait for it from a thunk
 */
export function fetchCurrencyConversionRate(
  baseCurrencyCode: string,
  targetCurrencyCode: string,
  onComplete: (result: ApiActionResult<number>) => void = noop,
): AppAction {
  return (dispatch, getState) => {
    if (baseCurrencyCode === targetCurrencyCode) {
      onComplete({ status: 'success', data: 1 })
      return
    }
    const key = getCurrencyConversionRateKey(baseCurrencyCode, targetCurrencyCode)
    const stored = getState().geo.currencyConversionRates[key]
    if (
      stored?.rate !== undefined &&
      stored.lastFetchedAt &&
      Date.now() - stored.lastFetchedAt < CURRENCY_CONVERSION_RATE_MAX_AGE_MS
    ) {
      onComplete({ status: 'success', data: stored.rate })
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_CURRENCY_CONVERSION_RATE,
      key,
      request: () => convertCurrency(1, baseCurrencyCode, targetCurrencyCode),
      onComplete,
    })
  }
}
